import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { collapseAnimation } from '@myarvato/shared/animations';
import { DataFieldComponent } from '@myarvato/shared/data-field';
import { LocalizedDecimalPipe } from '@myarvato/shared/localized-decimal-pipe';
import { DayjsPipe, DaysAgoPipe } from '@myarvato/shared/pipelines';
import { BreakpointService } from '@myarvato/shared/services';
import { TranslatePipe } from '@ngx-translate/core';
import { Timeline } from 'primeng/timeline';
import { ButtonModule } from 'primeng/button';
import { DetailViewDto, EventBaseDto, EventDto } from '@myarvato/modules/transport/models';
import { CommentComponent } from './components/comment/comment.component';
import { EventComponent } from './components/event/event.component';
import { ExceptionStatusChangeComponent } from './components/exception-status-change/exception-status-change.component';

@Component({
  selector: 'transport-details-event-history',
  imports: [
    CommonModule,
    TranslatePipe,
    DataFieldComponent,
    DayjsPipe,
    Timeline,
    ButtonModule,
    DaysAgoPipe,
    FormsModule,
    EventComponent,
    CommentComponent,
    ExceptionStatusChangeComponent,
    LocalizedDecimalPipe,
  ],
  templateUrl: './details-event-history.component.html',
  styleUrl: './details-event-history.component.scss',
  animations: [collapseAnimation],
})
export class DetailsEventHistoryComponent {
  // Inputs
  public readonly details = input.required<DetailViewDto>();

  // Injections
  private readonly breakpointService = inject(BreakpointService);

  // Change detection
  protected readonly isComponentCollapsed = signal<boolean>(true);

  protected readonly isEmptyState = computed(() => (this.details().eventInfo.events.length ?? 0) <= 0);

  protected readonly timelineAlign = computed(() => {
    const breakpoint = this.breakpointService.currentBreakpoint();
    return breakpoint === 'xs' || breakpoint === 'sm' ? 'right' : 'alternate';
  });

  protected readonly timelineItems = computed(() => {
    const events = [...(this.details().eventInfo.events ?? [])];
    const align = this.timelineAlign();

    return events
      .filter((event) => event.$type !== 'EventDto' || !(event as EventDto).expectedEvent)
      .sort((eventA, eventB) => {
        const timestampA = eventA.timestamp ? new Date(eventA.timestamp).getTime() : 0;
        const timestampB = eventB.timestamp ? new Date(eventB.timestamp).getTime() : 0;
        return timestampB - timestampA;
      })
      .map((event, index) => ({
        event,
        severityClass: this.getSeverityClass(event),
        iconClass: this.getIconClass(event),
        markerAppearance: this.getMarkerAppearance(event),
        side: align === 'alternate' ? (index % 2 === 0 ? 'right' : 'left') : 'left',
      }));
  });
  protected toggleComponentCollapse(): void {
    this.isComponentCollapsed.set(!this.isComponentCollapsed());
  }

  private getSeverityClass(eventBaseDto: EventBaseDto): string {
    if (eventBaseDto.$type === 'ExceptionStatusChangeDto') {
      return 'info';
    }

    if (eventBaseDto.$type === 'CommentDto') {
      return 'default';
    }

    const event = eventBaseDto as EventDto;
    const severity = (event.severity ?? '').toUpperCase();
    if (severity.includes('EXCEPTION')) {
      return 'danger';
    }

    return 'success';
  }

  private getIconClass(eventBaseDto: EventBaseDto): string {
    if (eventBaseDto.$type === 'ExceptionStatusChangeDto') {
      return 'pi pi-info-circle';
    }

    if (eventBaseDto.$type === 'CommentDto') {
      return 'pi pi-circle';
    }

    const event = eventBaseDto as EventDto;
    const severity = (event.severity ?? '').toUpperCase();
    return severity.includes('EXCEPTION') ? 'pi pi-exclamation-circle' : 'pi pi-check';
  }

  private getMarkerAppearance(eventBaseDto: EventBaseDto): string {
    if (eventBaseDto.$type !== 'EventDto') {
      return 'icon-only';
    }

    const event = eventBaseDto as EventDto;
    const severity = (event.severity ?? '').toUpperCase();
    if (severity.includes('EXCEPTION')) {
      return 'icon-only';
    }

    return 'outline';
  }
}
